import { ReactNode, createContext, useMemo, useState } from 'react'

const STORAGE_KEY = 'signup-draft'

type SignupDraft = {
  fullName: string
  taxId: string
  email: string
}

type SignupContextValue = {
  draft: SignupDraft
  setFullName: (fullName: string) => void
  setTaxId: (taxId: string) => void
  setEmail: (email: string) => void
  clearDraft: () => void
}

export const SignupContext = createContext<SignupContextValue | null>(null)

const emptyDraft = (): SignupDraft => ({ fullName: '', taxId: '', email: '' })

function readDraft(): SignupDraft {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return emptyDraft()
    const parsed = JSON.parse(raw) as Partial<SignupDraft>
    return {
      fullName: parsed.fullName ?? '',
      taxId: parsed.taxId ?? '',
      email: parsed.email ?? '',
    }
  } catch {
    return emptyDraft()
  }
}

function writeDraft(draft: SignupDraft) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
}

export function SignupProvider({ children }: { children: ReactNode }) {
  const [draft, setDraft] = useState<SignupDraft>(readDraft)

  const value = useMemo<SignupContextValue>(
    () => ({
      draft,
      setFullName: (fullName) => {
        setDraft((current) => {
          const next = { ...current, fullName }
          writeDraft(next)
          return next
        })
      },
      setTaxId: (taxId) => {
        setDraft((current) => {
          const next = { ...current, taxId }
          writeDraft(next)
          return next
        })
      },
      setEmail: (email) => {
        setDraft((current) => {
          const next = { ...current, email }
          writeDraft(next)
          return next
        })
      },
      clearDraft: () => {
        sessionStorage.removeItem(STORAGE_KEY)
        setDraft(emptyDraft())
      },
    }),
    [draft],
  )

  return (
    <SignupContext.Provider value={value}>{children}</SignupContext.Provider>
  )
}
